import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';
import { ChevronDownIcon } from './public/icons';
import { useView } from '../context/ViewContext';
import { logoutSession, getValidToken } from '../utils/auth';
import { useActiveBusiness } from '../hooks/useActiveBusiness';
import { DEFAULT_DASHBOARD_PATH } from '../utils/workspaceAuthorization';
import { resolveBookingPagePath } from '../utils/bookingPageLink';
import { hasTeamModeAccess } from '../utils/teamModeAccess';
import { useDiscoveryStatus } from '../hooks/useDiscoveryStatus';
import { DISCOVERY_URL } from '../constants/discovery';
import {
  canUseProfessionalFeatures,
  getAllowedViewsForAuthPayload,
  isPlatformAdminPayload,
} from '../utils/roleCapabilities';

const PUBLIC_LINKS = [
  { to: '/pricing', label: 'Pricing' },
  { to: '/why-haastia', label: 'Why Haastia' },
  { to: '/about', label: 'About' },
  { to: '/help', label: 'Help' },
];

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentView } = useView();
  const auth = getValidToken();
  const { enabled: discoveryEnabled } = useDiscoveryStatus();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [hasActiveBookingPage, setHasActiveBookingPage] = useState(false);
  const [showOnboardingOption, setShowOnboardingOption] = useState(false);
  const userMenuRef = useRef(null);

  const user = auth?.payload ?? null;
  const userName = user?.name ?? null;
  const userUsername = user?.username ?? null;
  const role = user?.role ?? null;
  const userId = user?._id || user?.id || null;
  const authToken = auth?.token || null;
  const canAccessProfessionalFeatures = canUseProfessionalFeatures(role);
  const canAccessPlatformAdmin = isPlatformAdminPayload(user);
  const { business: activeBusiness, capabilities: workspaceCapabilities } = useActiveBusiness();

  // getAllowedViewsForAuthPayload already injects the professional view for a
  // session carrying businessWorkspaceAccess, so a workspace member keeps a way
  // back into the dashboard even while their account role stays 'customer'.
  const canAccessWorkspaceDashboard = getAllowedViewsForAuthPayload(user).includes('professional');
  // Recruiters and platform admins render their own dashboard entries below.
  const showWorkspaceDashboard =
    canAccessWorkspaceDashboard && !canAccessPlatformAdmin && role !== 'recruiter';
  const workspaceLandingPath = workspaceCapabilities?.workspaceLandingPath || DEFAULT_DASHBOARD_PATH;
  // A manager without a provider identity lands on /team; single-mode
  // professionals have no team workspace at all, so the guard would reject them.
  const showTeamWorkspaceLink =
    canAccessWorkspaceDashboard && hasTeamModeAccess(activeBusiness);

  useEffect(() => {
    let isMounted = true;

    const syncBookingPageState = async () => {
      if (!canAccessProfessionalFeatures || !userId) {
        if (isMounted) {
          setHasActiveBookingPage(false);
          setShowOnboardingOption(false);
        }
        return;
      }

      try {
        // Signed in as the owner, the profile answers with the owner-only
        // `setup` summary: the page is live as soon as the account is, and
        // `setup.bookable` says whether clients can book it yet.
        const response = await axios.get(`/api/professional/${userId}`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
        });
        if (!isMounted) return;
        const data = response?.data || {};
        // An owner of a paused, hidden or inactive page gets a preview notice
        // instead; that page is not live, so there is no link to it here.
        setHasActiveBookingPage(!data.preview);
        setShowOnboardingOption(data.setup?.bookable === false);
      } catch {
        if (!isMounted) return;
        setHasActiveBookingPage(false);
        setShowOnboardingOption(false);
      }
    };

    syncBookingPageState();

    return () => {
      isMounted = false;
    };
  }, [authToken, canAccessProfessionalFeatures, userId]);

  // Close both menus whenever the route changes.
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  // Close the account menu on an outside click or Escape.
  useEffect(() => {
    if (!isUserMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsUserMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isUserMenuOpen]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleLogout = async () => {
    await logoutSession();
    navigate('/login');
  };

  const handleMyBookingPage = () => {
    const bookingPagePath = resolveBookingPagePath(activeBusiness, {
      username: userUsername,
      _id: userId,
    });
    if (!bookingPagePath) return;
    navigate(bookingPagePath);
  };

  const handleWorkspaceDashboard = () => {
    setCurrentView('professional');
    navigate(workspaceLandingPath);
  };

  const handleCompleteOnboarding = () => {
    setCurrentView('professional');
    navigate('/onboarding');
  };

  const userInitial = userName ? userName.trim().charAt(0).toUpperCase() : '';

  return (
    <nav className="navbar" aria-label="Primary">
      <Link to="/" className="logo navbar-logo" onClick={closeMobileMenu}>
        haastia
      </Link>

      <div
        id="public-navbar-menu"
        className={`navbar-links${isMobileMenuOpen ? ' is-open' : ''}`}
      >
        <div className="navbar-nav">
          {discoveryEnabled && (
            <a className="navbar-link" href={DISCOVERY_URL} onClick={closeMobileMenu}>
              Discover
            </a>
          )}
          {PUBLIC_LINKS.map(({ to, label }) => (
            <NavLink key={to} to={to} className="navbar-link" onClick={closeMobileMenu}>
              {label}
            </NavLink>
          ))}
        </div>

        {!userName && (
          <div className="navbar-auth">
            <Link to="/login" className="btn btn-secondary btn-sm" onClick={closeMobileMenu}>
              Log in
            </Link>
            <Link to="/signup" className="btn btn-primary btn-sm" onClick={closeMobileMenu}>
              Start free trial
            </Link>
          </div>
        )}
      </div>

      {userName && (
        <div className={`user-menu${isUserMenuOpen ? ' is-open' : ''}`} ref={userMenuRef}>
          <button
            type="button"
            className="user-menu__trigger"
            aria-haspopup="true"
            aria-expanded={isUserMenuOpen}
            aria-controls="navbar-user-menu"
            onClick={() => setIsUserMenuOpen((isOpen) => !isOpen)}
          >
            <span className="user-menu__avatar" aria-hidden="true">{userInitial}</span>
            <span className="user-name">{userName}</span>
            <ChevronDownIcon size={15} className="user-menu__chevron" />
          </button>

          <div className="dropdown-content" id="navbar-user-menu">
            {showWorkspaceDashboard && (
              <button className="dropdown-item" onClick={handleWorkspaceDashboard}>
                Dashboard
              </button>
            )}

            {role === 'professional' && (
              <button
                className="dropdown-item"
                onClick={() => navigate('/pro/settings')}
              >
                Settings
              </button>
            )}

            {showTeamWorkspaceLink && (
              <button
                className="dropdown-item"
                onClick={() => navigate('/team')}
              >
                Team
              </button>
            )}

            {canAccessPlatformAdmin && (
              <>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setCurrentView('professional');
                    navigate(DEFAULT_DASHBOARD_PATH);
                  }}
                >
                  View dashboard
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setCurrentView('admin');
                    navigate('/admin');
                  }}
                >
                  Admin Dashboard
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setCurrentView('admin');
                    navigate('/recruiter');
                  }}
                >
                  Referrals
                </button>
              </>
            )}

            {role === 'recruiter' && (
              <>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setCurrentView('professional');
                    navigate(DEFAULT_DASHBOARD_PATH);
                  }}
                >
                  Dashboard
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setCurrentView('professional');
                    navigate('/recruiter');
                  }}
                >
                  Referrals
                </button>
              </>
            )}

            {canAccessProfessionalFeatures && hasActiveBookingPage && (
              <button className="dropdown-item" onClick={handleMyBookingPage}>
                My booking page
              </button>
            )}

            {canAccessProfessionalFeatures && showOnboardingOption && (
              <button className="dropdown-item" onClick={handleCompleteOnboarding}>
                Complete onboarding
              </button>
            )}

            <button onClick={handleLogout} className="dropdown-item dropdown-item--logout">
              Logout
            </button>
          </div>
        </div>
      )}

      <button
        className={`navbar-burger${isMobileMenuOpen ? ' is-open' : ''}`}
        aria-label="Toggle navigation menu"
        aria-controls="public-navbar-menu"
        aria-expanded={isMobileMenuOpen}
        type="button"
        onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
      >
        <span />
        <span />
        <span />
      </button>
    </nav>
  );
}

export default Navbar;
